import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const Field = ({ f, value, onChange, testid }) => {
  const id = `${testid}-${f.name}`;
  if (f.type === "select")
    return (
      <select id={id} data-testid={id} className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={f.required}>
        {f.placeholder && <option value="">{f.placeholder}</option>}
        {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    );
  if (f.type === "textarea")
    return <Textarea id={id} data-testid={id} rows={f.rows || 4} className="bg-zinc-900 border-zinc-800" value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={f.required} placeholder={f.placeholder} />;
  if (f.type === "checkbox")
    return <input id={id} data-testid={id} type="checkbox" className="h-5 w-5 accent-amber-500" checked={!!value} onChange={(e) => onChange(e.target.checked)} />;
  return <Input id={id} data-testid={id} type={f.type || "text"} step={f.type === "number" ? "any" : undefined} className="bg-zinc-900 border-zinc-800" value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={f.required} placeholder={f.placeholder} />;
};

export const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, testid = "form", submitLabel = "Salvar" }) => {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setV(initial || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const body = {};
    fields.forEach((f) => {
      let x = v[f.name];
      if (f.type === "number") x = x === "" || x == null ? null : Number(x);
      else if (f.type === "checkbox") x = !!x;
      else x = x ?? f.default ?? "";
      body[f.name] = x;
    });
    setBusy(true);
    const ok = await onSubmit(body);
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle className="font-display text-xl">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={f.half ? "col-span-1 space-y-1.5" : "col-span-2 space-y-1.5"}>
              <Label htmlFor={`${testid}-${f.name}`} className="text-xs text-zinc-400">{f.label}{f.required && " *"}</Label>
              <Field f={f} value={v[f.name]} onChange={(x) => setV((s) => ({ ...s, [f.name]: x }))} testid={testid} />
            </div>
          ))}
          <Button type="submit" disabled={busy} className="btn-gold col-span-2 h-11 rounded-full" data-testid={`${testid}-submit`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
